import { Lci3SalesMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { ValueInput, formatters } from '@utils/formattersV2';
import { WaterfallChartProps } from '@features/campaigns/subdomains/visitation/configuration/waterfallChartOptions';
import { clampNegativeToZero } from '@utils/valueTransformers';

type Lci3SalesMetric = keyof Lci3SalesMetrics;
type ChannelName = 'total' | 'inStore' | 'online';
type LiftCategory = (typeof liftCategories)[number];

const liftCategories = [
  'transactionSizeLift',
  'transactionRateLift',
  'penetrationLift',
  'incrementalSalesLift',
] as const satisfies readonly Lci3SalesMetric[];

const salesCategories = [
  'transactionSizeSales',
  'transactionRateSales',
  'penetrationSales',
  'incrementalSales',
] as const satisfies readonly Lci3SalesMetric[];

const categoryToName: Record<LiftCategory, string> = {
  transactionSizeLift: 'Transaction Size',
  transactionRateLift: 'Transaction Rate',
  penetrationLift: 'Penetration',
  incrementalSalesLift: 'Incrementality',
};

const liftCategoriesToWaterfallData = (
  channel: ChannelName,
  salesMetrics: Lci3SalesMetrics
): WaterfallChartProps['data'] =>
  liftCategories.map((category, index) => {
    const value = salesMetrics[category][channel] ?? 0;
    const salesCategory = salesCategories[index];
    if (!salesCategory) {
      const message = `No corresponding sales category found for lift category: ${category}`;
      console.error(message);
      throw new Error(message);
    }
    return {
      name: categoryToName[category],
      value: salesMetrics[salesCategory][channel] ?? 0,
      dataLabelValue:
        index === liftCategories.length - 1
          ? clampNegativeToZero(value)
          : value,
    };
  });

export const liftCategoriesToWaterfallChartProps = (
  channel: ChannelName,
  salesMetrics: Lci3SalesMetrics
): WaterfallChartProps => {
  return {
    data: liftCategoriesToWaterfallData(channel, salesMetrics),
    valueFormatter: waterfallValueFormatter,
    yAxisTitle: 'Sales Lift',
    yAxisFormatter: waterfallYAxisFormatter,
  };
};

const waterfallValueFormatter = (value: ValueInput) =>
  formatters.percentage(value, { decimals: 1 });

const waterfallYAxisFormatter = (value: ValueInput) =>
  formatters.percentage(value, { decimals: 1 });
